// @LTG-ID: data/journals/where-to-start-business-value-exit
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 40 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Series capstone (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "where-to-start-business-value-exit",
    FRONTMATTER: {
        number: "77",
        section: "Series capstone",
        sectionHref: "/journals#series-capstone",
        sectionAnchor: "series-capstone",
        title: "Where Do I Start? A Reading Map for Owners Thinking About Value and Exit",
        deck: "Start exit planning with three steps: establish what your business is worth today with a market-based estimate, identify which value drivers are holding it below its potential, and calculate your personal walk-away number.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-07-06",
        publishedHuman: "July 6, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 496,
        timeRequired: "PT3M",
        keywords: "how to start exit planning, business exit planning steps, prepare to sell my business where to start, business value first steps",
        articleSection: "Series capstone",
        articleBodyPreview: "Start exit planning with three steps: establish what your business is worth today with a market based estimate, identify which value drivers are holding it below its potential, and calculate your personal walk away number. Those three answers determine your timeline, your work list, and whether the market's price and your needs align. Forty articles is a library, not a starting point. This page is the map, organized around the five questions every owner eventually asks, in the order they are bes",
        complianceNote: "This article is educational and does not constitute a valuation engagement or financial, legal, or tax advice.",
        needsDisclaimerFooter: true,
        internalLinks: [
        "how-much-is-my-business-worth",
        "exit-planning-guide",
        "what-is-ebitda",
        "sde-vs-ebitda-guide",
        "what-is-a-valuation-multiple",
        "how-long-does-it-take-to-sell-a-business",
        "what-percentage-of-businesses-sell",
        "owner-dependence-business-value",
        "asset-sale-vs-stock-sale",
        "silver-tsunami-business-statistics",
        "business-value-drivers",
        "recurring-revenue-business-value",
        "customer-concentration-selling-business",
        "clean-financial-records-business-value",
        "management-team-business-value",
        "business-systems-processes-value",
        "does-growth-increase-business-value",
        "add-backs-selling-a-business",
        "due-diligence-selling-a-business",
        "increase-business-value-before-selling",
        "personal-readiness-to-exit",
        "walk-away-number-selling-business",
        "life-after-selling-business",
        "seller-financing-business-sale",
        "earnout-business-sale",
        "sba-financing-business-sale",
        "family-succession-vs-selling",
        "sell-business-to-employees",
        "sell-business-confidentially",
        "do-i-need-a-business-broker",
        "selling-hvac-plumbing-electrical-business",
        "selling-a-dental-practice",
        "selling-a-medical-practice",
        "selling-a-restaurant",
        "selling-a-retail-business",
        "selling-a-manufacturing-business",
        "selling-an-auto-repair-shop",
        "selling-a-professional-services-firm",
        "selling-a-construction-company"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Where Do I Start? A Reading Map for Value & Exit · LTG",
        canonical: "https://letstalkgrowth.com/journals/where-to-start-business-value-exit",
        ogImage: "/journals/og/where-to-start-business-value-exit.jpg",
    },
    BODY_MARKDOWN: `Start exit planning with three steps: establish what your business is worth today with a market-based estimate, identify which value drivers are holding it below its potential, and calculate your personal walk-away number. Those three answers determine your timeline, your work list, and whether the market's price and your needs align.

Forty articles is a library, not a starting point. This page is the map, organized around the five questions every owner eventually asks, in the order they are best asked.

## Question 1: What is it worth?

Begin with the valuation primer, [How Much Is My Business Worth](/journals/how-much-is-my-business-worth), and its supporting cast: the [EBITDA](/journals/what-is-ebitda) and [SDE](/journals/sde-vs-ebitda-guide) explainers, the [multiples article](/journals/what-is-a-valuation-multiple), and the add-backs guide. Together they explain the arithmetic buyers actually use and the vocabulary you will hear from every advisor. The point of this station is not the number itself; it is understanding what produces the number, because that is the list of things you can change.

## Question 2: What drives the value up or down?

The [value drivers pillar](/journals/business-value-drivers) and its cluster, recurring revenue, customer concentration, financial records, team depth, systems, growth, and above all [owner dependence](/journals/owner-dependence-business-value), form the working heart of the series. If you read only one cluster, read this one, and finish with the 24-month checklist, which compresses the whole program into a sequence you can run.

## Question 3: Am I ready, personally?

The most skipped station and the most consequential. The [personal readiness pillar](/journals/personal-readiness-to-exit), the [walk-away number calculation](/journals/walk-away-number-selling-business), and the [life-after-sale article](/journals/life-after-selling-business) address the finding this series cites repeatedly: seller regret comes overwhelmingly from personal unpreparedness, not deal terms. The math and the meaning both belong in the plan.

## Question 4: How do deals actually work?

The process cluster demystifies the transaction: how long sales take, why so many listed businesses never sell, [due diligence](/journals/due-diligence-selling-a-business), confidentiality, seller financing, earnouts, SBA-financed buyers, the broker decision, asset versus stock structure, and the exit-path comparisons, family, employees, or market. Read these before the letter of intent, not during it.

## Question 5: What is specific to my industry?

The industry set translates everything above into the language of your sector: trades and home services, dental, medical, restaurants, retail, manufacturing, auto services, professional services, and construction. Each article covers the buyer landscape, the sector's distinctive value drivers, and its particular trapdoors, licenses, leases, concentration, environmental, so nothing arrives as a surprise.

## The three-step start

If the library still feels large, the entry sequence is three steps. Establish today's value with a market-based estimate, honestly derived from real numbers. Identify your two or three weakest value drivers, the checklist article will surface them quickly. And run the walk-away math, so you know whether the market's answer and your life's requirements already align, or how far apart they sit. Everything else in exit planning is scheduling: those three answers set the timeline, and this Journal will be here for each station as you reach it.`,
    FAQ: [
        { question: "What is the first step in exit planning?", answer: "Establish what the business is worth today with a market-based estimate grounded in real financials. Value is the reference point every other decision, timing, improvement priorities, and path selection, depends on." },
        { question: "How long before selling should I start reading and preparing?", answer: "Three to five years out is ideal, because the improvements that most affect price, reducing owner dependence and building recurring revenue, take years to show in the trailing results buyers evaluate. Starting later still helps; starting earlier helps more." },
        { question: "Do I need all of this if I am not sure I will ever sell?", answer: "The same drivers that raise sale value, provable earnings, team depth, recurring revenue, systems, also make a business more profitable and easier to own now. Exit preparation is simply good management with a deadline attached." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Valuation",
            title: "What Is EBITDA? A Plain-English Guide for Business Owners",
            meta: "3 min · Brief 40",
            to: "/journals/what-is-ebitda",
        },
    ],
};

export default BRIEF;
